// The HN-facing essay: "Sheet music is one glyph away from Turing-complete",
// with the LIVE Dal Segno Machine embedded mid-article. This page is the
// shareable front door — the reader plays the widget in place, then discovers
// the workbench it lives in. Editorial voice (blog, first person), not the
// SIGBOVIK register (that draft lives in docs/); same mathematics.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DalSegnoMachineExhibit } from '@/components/lab/DalSegnoMachineExhibit';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

const TITLE = 'Sheet music is one glyph away from Turing-complete';

/** Musical furniture drawn with the bundled Bravura face (SMuFL codepoints). */
function G({ t }: { t: string }) {
  return <span className="segno-glyph">{t}</span>;
}
const RL = '\ue040'; // repeat-left barline
const RR = '\ue041'; // repeat-right barline
const SG = '\ue047'; // segno

export function ArticleSegnoPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = `${TITLE} · Voicing Workbench`;
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <article className="drop2-page article-page" data-testid="article-segno">
      <header className="drop2-hero">
        <p className="learn-eyebrow">Lab · essay</p>
        <h1>{TITLE}</h1>
        <p>
          Repeat signs, voltas and <em>dal segno</em> form a real control-flow language — one that
          provably always halts. Add a single invented mark and rehearsal letters become
          registers, scores become programs, and &ldquo;does this piece end?&rdquo; becomes the
          halting problem. There&apos;s a playable machine halfway down.
        </p>
      </header>

      <section className="drop2-section">
        <h2>Notation is a programming language that always halts</h2>
        <p>
          Musicians have always known notation is code; they just call the interpreter &ldquo;the
          band.&rdquo; Bars run top to bottom, <strong><G t={RL} /> <G t={RR} /></strong> brackets a loop body,{' '}
          <strong>1.</strong> and <strong>2.</strong> volta brackets branch on the loop index, and{' '}
          <strong>D.S. al Coda</strong> is a GOTO with a label — a construct respectable languages
          abandoned in 1968 and musicians still sight-read.
        </p>
        <p>
          I found the interesting part while building the opposite of a compiler: a{' '}
          <em>flattener</em>. This site&apos;s workbench expands any lead sheet — repeats, voltas,
          D.C. al Coda, &ldquo;3x&rdquo; — into the literal order you&apos;d perform it, so the
          voice-leading engine and the playback band can walk one straight line of bars. That
          function is <em>total</em>: it never hangs, on any chart, with no timeout. It processes
          every chart in the library without ever being asked to prove it will finish.
        </p>
        <p>
          It took me embarrassingly long to notice that this is a theorem. Every conditional in
          standard notation branches on one variable — <em>which pass is this?</em> — and only ever
          compares it against constants inked on the page (&ldquo;play 3 times&rdquo;,
          &ldquo;2nd ending&rdquo;). Nothing a performer does can <em>write</em> a number anywhere.
          The performer&apos;s whole state is a position and a few bounded counters. In the terms
          of Hofstadter&apos;s <em>Gödel, Escher, Bach</em>, notation is a BlooP language: loops,
          but only bounded ones. Every score is a finite state machine. Every piece provably ends.
        </p>
        <p>
          Which is the correct design for music. Concerts should end. But it raises a precise
          question: how far is notation from <em>not</em> having this property — from being able to
          compute anything a computer can?
        </p>
      </section>

      <section className="drop2-section">
        <h2>The missing instruction</h2>
        <p>
          The answer turns out to be: <strong>one glyph</strong>. Notation has loops, branches and
          jumps — everything except <em>feedback</em>. No count can depend on another count. So
          invent the smallest mark that adds it:
        </p>
        <p className="article-glyph-display">
          <G t={RL} /> A <G t={RR} /> ×(B−1)
          <span className="article-glyph-caption">
            &ldquo;repeat section A one time fewer than B was just repeated&rdquo;
          </span>
        </p>
        <p>Three rules, all riding on furniture already printed on every chart:</p>
        <ol>
          <li>
            <strong>Rehearsal letters are registers.</strong> Each boxed letter holds a number: the
            count of that section&apos;s most recent complete traversal. They were registers all
            along, waiting.
          </li>
          <li>
            <strong>Repeat counts are expressions.</strong> A constant <code>×3</code>, the
            soloist&apos;s choice <code>×n</code> (an open vamp — the program&apos;s input), or the
            new referential form <code>×(A−1)</code>. A count of zero is a <em>tacet</em>: skip the
            section, write 0.
          </li>
          <li>
            <strong>Directions take guards.</strong> The dal segno family gets a condition, in
            Italian like every other instruction: <em>D.S. se A &gt; 0</em>. &ldquo;From the sign,
            if A remains.&rdquo;
          </li>
        </ol>
        <p>
          Read a register, write a register, add or subtract one, branch on zero. If you&apos;ve
          met theoretical computer science, the hair on your neck is already up: those are exactly
          the instructions of a <strong>Minsky counter machine</strong> (1967), and two counters
          are enough for Turing completeness. Two vamps and a conditional D.S. and sheet music can
          compute anything computable.
        </p>
        <p>Every Minsky instruction compiles to a line of score:</p>
        <div className="article-table-wrap">
          <table className="article-table">
            <thead>
              <tr>
                <th>Counter machine</th>
                <th>Notation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>inc(A)</code>
                </td>
                <td><G t={RL} /> A <G t={RR} /> ×(A+1)</td>
              </tr>
              <tr>
                <td>
                  <code>dec(A)</code>
                </td>
                <td><G t={RL} /> A <G t={RR} /> ×(A−1)</td>
              </tr>
              <tr>
                <td>
                  <code>jz(A, ℓ)</code>
                </td>
                <td>
                  <G t={SG} /> <em>al №ℓ se A = 0</em>
                </td>
              </tr>
              <tr>
                <td>
                  <code>halt</code>
                </td>
                <td>
                  ⊕ <em>Fine</em>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          The first real program you can write is a while loop — <em>while A &gt; 0: play B once,
          replay A one time fewer</em>. B lands exactly <em>n</em> times, and you can hear the vamp
          shrink each pass: <em>n</em> bars, then <em>n</em>−1, down to a silent tacet and out.
          Counter machines compute in unary, and here unary time is literally musical time —
          counting down from <em>n</em> costs Θ(n²) bars of everyone&apos;s evening. You can{' '}
          <em>hear</em> the asymptotics.
        </p>
      </section>

      <section className="drop2-section">
        <h2>Play it</h2>
        <p>
          This is the machine, live. Five pieces: a call-and-response that copies a register, the
          while-loop countdown, addition by transfer loop, a one-bar piece that provably never ends
          — and <strong>Ulam&apos;s Vamp</strong>, whose performance computes the Collatz map. Pick
          an opening <em>n</em>, press Perform (or skip to the Fine), and watch the registers.
        </p>
        <div className="article-widget">
          <DalSegnoMachineExhibit />
        </div>
        <p>
          Ulam&apos;s Vamp is sixteen lines. Even values halve (pairs of the A vamp bank into B);
          odd values rebuild as 3n+1 (six A&apos;s per banked pair, plus four); the <em>Fine</em>{' '}
          is reachable only at 1. The implementation is unit-tested to agree with the genuine
          Collatz chain length for every opening n = 1…15 — the score doesn&apos;t{' '}
          <em>resemble</em> the mathematics, it <em>performs</em> it. Which means whether any given
          evening&apos;s performance ends is precisely the Collatz conjecture for the
          soloist&apos;s opening choice. The program note writes itself:{' '}
          <em>Duration: conjecturally finite.</em>
        </p>
      </section>

      <section className="drop2-section">
        <h2>The fuel gauge is the point</h2>
        <p>
          The widget&apos;s fuel bar isn&apos;t a UI flourish. The moment ×(A−1) exists, my
          flattener can&apos;t be a total function any more — &ldquo;does this chart end?&rdquo;{' '}
          <em>is</em> the halting problem, and the honest type signature changes from{' '}
          <code>flatten(score): Bar[]</code> to <code>run(score, fuel): Trace | OutOfFuel</code>.
          Rice&apos;s theorem follows it in: every interesting question about an extended score —
          is the form 32 bars? does the coda ever play? — becomes undecidable in general. Analysis
          can only ever answer &ldquo;within the step budget.&rdquo;
        </p>
        <p>
          And this, inverted, explains something real about the notation we actually have. The
          avant-garde spent a century circling this boundary without crossing it: Satie&apos;s{' '}
          <em>Vexations</em> (repeat 840 times) is a big constant; Terry Riley&apos;s <em>In C</em>{' '}
          (repeat each cell as long as you like) is nondeterminism; the vamp-till-cue is external
          input. None of them have feedback — a count that reads a count — and that one arrow is
          the entire distance between an automaton and a universal machine. Standard notation was
          engineered, accidentally, over eight centuries, to always halt. For music that human
          beings must perform and then go home, that is the correct language design.
        </p>
      </section>

      <section className="drop2-section article-outro">
        <h2>Where this lives</h2>
        <p>
          The Dal Segno Machine is an exhibit in the <strong>Lab</strong> of a jazz
          voice-leading workbench I&apos;m building — a tool that takes any lead sheet and works
          out how to move drop-2 chords through it with the smoothest possible voice leading,
          engraves the result as notation, and plays it back with a band. The Lab is where the
          mathematics inside that problem gets drawn: chord synonyms, the Tonnetz, orbifolds,
          maximal evenness — and now one small proof that your fake book is nearly a computer.
        </p>
        <div className="article-cta-row">
          <Link className="btn" to={APP_ROUTES.lab}>
            Explore the Lab
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
          <Link className="btn" to={APP_ROUTES.workbench}>
            Open the Workbench
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </article>
  );
}
